import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { Store } from '@ngrx/store';

import { filter, take } from 'rxjs/operators';

import {
  selectError,
  selectSelectedCaNumber,
  selectSubmitting,
} from 'src/@store/products/ca-numbers/ca-numbers.selectors';

import { CaNumbersActions } from '../../../../../../@store/products/ca-numbers/ca-numbers.actions';
import { SingleCaNumberDetails } from '../../../../../../@store/products/ca-numbers/ca-numbers.model';

@Component({
  selector: 'ciwc-order-create-new-ca',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButton,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './order-create-new-ca.component.html',
  styleUrl: './order-create-new-ca.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderCreateNewCaComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<OrderCreateNewCaComponent>);
  private readonly data = inject(MAT_DIALOG_DATA);

  form: FormGroup = this.fb.group({
    caNumber: ['', [Validators.required]],
  });

  submitting = this.store.selectSignal(selectSubmitting);
  error = this.store.selectSignal(selectError);
  submitted = false;

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.submitted = true;
    if (this.form.valid && !this.submitting()) {
      const parentForm = this.data.form;
      const jobDetails = parentForm.get('jobDetails')?.value;
      const generalFormValue = parentForm.get('general')?.value;

      // Format dates to YYYY-MM-DD using local time
      const formatDate = (date: Date | null) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const shippingDetailsValue = parentForm.get('shippingDetails')?.value;
      const shippingDetails = shippingDetailsValue ? [shippingDetailsValue] : [];

      const shipmentsValue = parentForm.get('shipments')?.value || [];
      const shipments = shipmentsValue.map((shipment: any) => ({
        ...shipment,
        dateShipped: shipment.dateShipped ? formatDate(new Date(shipment.dateShipped)) : null,
        dropShipment: shipment.dropShipment ? 1 : 0,
      }));

      const orderToSave = {
        ...generalFormValue,
        ...jobDetails,
        caCustomDescription: parentForm.get('caCustomDescription')?.value,
        id: this.data.orderId,
        promisedDate: formatDate(jobDetails?.promisedDate),
        expediteDate: formatDate(jobDetails?.expediteDate),
        materialsReceived: parentForm.get('materialsReceived')?.value || false,
        materials:
          parentForm.get('materials')?.value?.map((material: any) => ({
            id: material.id || 0,
            category: material.category,
            description: material.description,
            quantity: material.quantity,
            poNo: material.poNo,
            expDate: material.expDate,
            recDate: formatDate(material.recDate),
            status: material.status,
            notes: material.notes,
            attachments:
              material.attachments?.map((attachment: any) => ({
                name: attachment.attachmentName,
                document: attachment.attachmentDocument,
              })) || [],
          })) || [],
        followUps:
          parentForm.get('jobDetails.followUps')?.value?.map((followUp: any) => ({
            id: followUp.id || 0,
            dateFollowUp: formatDate(followUp.dateFollowUp),
            priorFirstDate: formatDate(followUp.priorFirstDate),
            priorSecondDate: formatDate(followUp.priorSecondDate),
            priorThirdDate: formatDate(followUp.priorThirdDate),
            notes: followUp.notes || '',
            ciw: followUp.ciw ? 1 : 0,
            cfm: followUp.cfm ? 1 : 0,
          })) || [],
        itemProcesses: this.data.itemProcesses || [],
        constructionSteps: this.data.constructionSteps || [],
        clientPartNo: parentForm.get('clientPartNo')?.value || '',
        feetPerReel: parentForm.get('feetPerReel')?.value || '',
        awg: parentForm.get('specification')?.get('awg')?.value || [],
        stranding: parentForm.get('specification')?.get('stranding')?.value || [],
        wireType: parentForm.get('specification')?.get('wireType')?.value || [],
        supplier: parentForm.get('specification')?.get('supplier')?.value || [],
        finishedDiameter: parentForm.get('specification')?.get('finishedDiameter')?.value || [],
        tip: parentForm.get('specification')?.get('tip')?.value || [],
        die: parentForm.get('specification')?.get('die')?.value || [],
        putUp: parentForm.get('specification')?.get('putUp')?.value || [],
        jacketMaterial: parentForm.get('specification')?.get('jacketMaterial')?.value || [],
        jacketColor: parentForm.get('specification')?.get('jacketColor')?.value || [],
        jacketPrinting: parentForm.get('specification')?.get('jacketPrinting')?.value || [],
        wallThickness: parentForm.get('specification')?.get('wallThickness')?.value || [],
        innerDiameter: parentForm.get('specification')?.get('innerDiameter')?.value || [],
        outerDiameter: parentForm.get('specification')?.get('outerDiameter')?.value || [],
        description: parentForm.get('specification')?.get('description')?.value || '',
        shippingDetails,
        shipments,
      };

      delete orderToSave.attachments;

      const payload = {
        ...orderToSave,
        caNumber: this.form.value.caNumber,
      };

      this.store.dispatch(CaNumbersActions.addCAFromOrder({ payload }));

      // After dispatching the action, send the CA number to the parent form
      this.sendCaToParentFormForPatching();
    }
  }

  /**
   * Sends the created CA number to the parent form for patching.
   */
  private sendCaToParentFormForPatching() {
    // Subscribe to the selected CA number only after dispatching the action
    this.store
      .select(selectSelectedCaNumber)
      .pipe(
        filter((caNumber: SingleCaNumberDetails | null) => !!caNumber),
        take(1),
      )
      .subscribe((caNumber: SingleCaNumberDetails | null) => {
        if (caNumber) {
          // Close the dialog and pass the CA number back to the parent component
          this.dialogRef.close({
            id: caNumber.id,
            caCustomDescription: this.form.get('caNumber')?.value,
          });
        }
      });
  }

  getErrorMessage(): string | null {
    const err = this.error();
    if (!this.submitted || !err) return null;
    if (typeof err === 'string') return err;
    if (err.error && typeof err.error === 'string') return err.error;
    if (err.error && err.error.errors) return err.error.errors;

    return 'An unknown error occurred.';
  }
}
